import { Footer } from "@/components/Footer";
import { OnboardingQuestion } from "@/components/OnboardingQuestion";
import { LoadingTransition } from "@/components/LoadingTransition";
import { ActiveFilters } from "@/components/filters/ActiveFilters";
import { MealCategories } from "@/components/home/MealCategories";
import { useHomePageAuth } from "@/hooks/useHomePageAuth";
import { useHomePageFilters } from "@/hooks/useHomePageFilters";
import { useHomePageOnboarding } from "@/hooks/useHomePageOnboarding";
import { useHomeMeals } from "@/hooks/useHomeMeals";
import { GridLoadingState } from "@/components/loading/GridLoadingState";
import { ProgressiveLocationDialog } from "@/components/location/ProgressiveLocationDialog";
import { useLocationServices } from "@/hooks/useLocationServices";
import { useEffect, useState, useCallback } from "react";
import { SearchHeader } from "@/components/SearchHeader";
import { useToast } from "@/hooks/use-toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HomeHeader } from "@/components/home/HomeHeader";

// Session storage key used in App.tsx for loading screen
const LOADING_SHOWN_KEY = 'homeplate_loading_shown';
const TIME_PREFERENCE_KEY = 'timePreferenceSet';

const Index = () => {
  const {
    toast
  } = useToast();
  const [hasError, setHasError] = useState(false);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const [pageInitialized, setPageInitialized] = useState(false);
  const {
    locationState,
    requestLocation,
    trackInteraction,
    shouldShowLocationPrompt,
    recordPromptShown,
    setMaybeLater,
    refreshLocation,
    doNotShowAgain
  } = useLocationServices();

  const userLocation = locationState.coords;
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationContextTrigger, setLocationContextTrigger] = useState<string | undefined>();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dialogSessionKey, setDialogSessionKey] = useState<string>('');

  const {
    activeFilters,
    setActiveFilters,
    removeFilter,
    clearQuery,
    clearDeliveryOnly,
    clearDistance,
    clearRating,
    clearPortions,
    clearDateRange
  } = useHomePageFilters();
  
  // Modified to check both session and local storage
  const hasCompletedInitialLoad = useState(() => {
    try {
      return !!sessionStorage.getItem(LOADING_SHOWN_KEY);
    } catch (error) {
      console.error('Error reading session storage:', error);
      return false;
    }
  })[0];
  
  const {
    showTransition,
    showOnboarding,
    handleTransitionComplete,
    handleTimePreference,
    selectedTimePreference,
    setShowOnboarding
  } = useHomePageOnboarding(setActiveFilters);

  const handleBrowse = useCallback(() => {
    try {
      localStorage.setItem(TIME_PREFERENCE_KEY, "true");
      localStorage.setItem("selectedTimePreference", "browse");
      sessionStorage.setItem(TIME_PREFERENCE_KEY, "true");
      sessionStorage.setItem("selectedTimePreference", "browse");
      setActiveFilters(prev => ({
        ...prev,
        dateRange: undefined
      }));
      setShowOnboarding(false);

      trackInteraction('browse_meals');
    } catch (error) {
      console.error('Error in handleBrowse:', error);
      toast({
        title: "Error",
        description: "Failed to save your preference. Please try again.",
        variant: "destructive"
      });
    }
  }, [setActiveFilters, setShowOnboarding, trackInteraction, toast]);

  const {
    data: meals = [],
    isLoading,
    error
  } = useHomeMeals(activeFilters, userLocation);

  useEffect(() => {
    try {
      if (locationState.permission === 'granted' && !locationState.isLoading) {
        refreshLocation();
      }
    } catch (error) {
      console.error('Error refreshing location:', error);
    }
  }, [locationState.permission, locationState.isLoading, refreshLocation]);

  useEffect(() => {
    if (!pageInitialized) {
      console.log("Index: Page initialized");
      setPageInitialized(true);
      setDialogSessionKey(Math.random().toString(36).substring(2, 11));
    }
  }, [pageInitialized]);

  useEffect(() => {
    try {
      const handleFilterOpen = () => {
        setIsFilterOpen(true);
        document.body.style.overflow = 'hidden';

        trackInteraction('open_filters');
      };
      const handleFilterClose = () => {
        setIsFilterOpen(false);
        document.body.style.overflow = 'auto';
      };
      window.addEventListener('filterOpen', handleFilterOpen);
      window.addEventListener('filterClose', handleFilterClose);
      return () => {
        window.removeEventListener('filterOpen', handleFilterOpen);
        window.removeEventListener('filterClose', handleFilterClose);
      };
    } catch (error) {
      console.error('Error setting up filter event listeners:', error);
    }
  }, [trackInteraction]);

  useEffect(() => {
    try {
      if (activeFilters.distance && activeFilters.distance > 0) {
        trackInteraction('use_distance_filter');

        if (
          locationState.permission === 'prompt' && 
          shouldShowLocationPrompt(true) && 
          !sessionStorage.getItem(`location_prompt_shown_${dialogSessionKey}`)
        ) {
          setLocationContextTrigger('distance_filter');
          setShowLocationPrompt(true);
          recordPromptShown();
          sessionStorage.setItem(`location_prompt_shown_${dialogSessionKey}`, 'true');
        }
      }
    } catch (error) {
      console.error('Error handling distance filter:', error);
    }
  }, [activeFilters.distance, locationState.permission, shouldShowLocationPrompt, recordPromptShown, trackInteraction, dialogSessionKey]);

  useEffect(() => {
    let timer: number | undefined;
    try {
      if (
        // Don't show location prompts during onboarding or transition
        !showTransition && 
        !showOnboarding && 
        locationState.permission === 'prompt' && 
        shouldShowLocationPrompt() && 
        !sessionStorage.getItem(`location_prompt_shown_${dialogSessionKey}`) &&
        !doNotShowAgain
      ) {
        // Changed from 5000 to 2000 milliseconds (2 seconds)
        timer = window.setTimeout(() => {
          setLocationContextTrigger(undefined);
          setShowLocationPrompt(true);
          recordPromptShown();
          sessionStorage.setItem(`location_prompt_shown_${dialogSessionKey}`, 'true');
        }, 2000);
      }
    } catch (error) {
      console.error('Error in location prompt effect:', error);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showTransition, showOnboarding, locationState.permission, shouldShowLocationPrompt, recordPromptShown, dialogSessionKey, doNotShowAgain]);

  useEffect(() => {
    try {
      if (!pageViewTracked) {
        trackInteraction('page_view');
        console.log('Index: Page view tracked');
        setPageViewTracked(true);
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }, [trackInteraction, pageViewTracked]);

  useEffect(() => {
    if (hasError && renderAttempt < 3) {
      const timer = setTimeout(() => {
        console.log(`Index: Attempting re-render (${renderAttempt + 1}/3)`);
        setHasError(false);
        setRenderAttempt(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasError, renderAttempt]);

  const handleEnableLocation = async () => {
    try {
      setShowLocationPrompt(false);
      await requestLocation();
    } catch (error) {
      console.error('Error requesting location:', error);
      toast({
        title: "Location Error",
        description: "There was a problem getting your location. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleMaybeLater = () => {
    try {
      setShowLocationPrompt(false);
      setMaybeLater();
      sessionStorage.setItem(`location_prompt_shown_${dialogSessionKey}`, 'true');
    } catch (error) {
      console.error('Error in handleMaybeLater:', error);
    }
  };

  if (error) {
    console.error('Error loading meals:', error);

    if (!hasError) {
      toast({
        title: "Error Loading Meals",
        description: "We had trouble loading meals. We'll try again.",
        variant: "destructive"
      });
      setHasError(true);
    }
    if (renderAttempt >= 3) {
      return <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-md">
            <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground">
              We're having trouble loading the meals. Please try again later.
            </p>
          </div>
        </div>;
    }
  }

  // Only show transition if App.tsx didn't already show it
  // and we need to show it specifically for this page
  if (showTransition && !hasCompletedInitialLoad) {
    console.log("Index: Showing transition screen");
    return <LoadingTransition onComplete={() => {
      console.log("Index: LoadingTransition complete, triggering handleTransitionComplete");
      handleTransitionComplete();
    }} />;
  }

  if (showOnboarding) {
    return <ErrorBoundary>
        <OnboardingQuestion questionText="When would you like your food?" options={["Today", "This Week", "Next Week"]} selectedAnswer={selectedTimePreference || ""} onSelect={handleTimePreference} onBrowse={handleBrowse} showBrowseOption={true} />
      </ErrorBoundary>;
  }

  return <ErrorBoundary>
      <div className="min-h-screen bg-background dark:bg-background pb-24 md:pb-0">
        <ProgressiveLocationDialog 
          open={showLocationPrompt} 
          onOpenChange={setShowLocationPrompt} 
          onEnableLocation={handleEnableLocation} 
          onMaybeLater={handleMaybeLater} 
          contextualTrigger={locationContextTrigger} 
        />
        
        <div className={`
            left-0 right-0 bg-background 
            ${!isFilterOpen ? 'relative md:sticky md:top-[3.5rem]' : 'relative'}
          `}>
          <HomeHeader />
          <div className="container mx-auto px-4 md:py-3 py-[2px]">
            <div className="mt-3">
              <ActiveFilters filters={activeFilters.filters} distance={activeFilters.distance} rating={activeFilters.rating} query={activeFilters.query} dateRange={activeFilters.dateRange} availablePortions={activeFilters.availablePortions} deliveryOnly={activeFilters.deliveryOnly} onRemoveFilter={removeFilter} onClearQuery={clearQuery} onClearDeliveryOnly={clearDeliveryOnly} onClearDistance={clearDistance} onClearRating={clearRating} onClearPortions={clearPortions} onClearDateRange={clearDateRange} />
            </div>
          </div>
        </div>
        
        <div className={`pt-2 md:pt-3 ${isFilterOpen ? 'pointer-events-none' : ''}`}>
          {isLoading ? <div className="container mx-auto px-4">
              <GridLoadingState />
            </div> : <MealCategories meals={meals || []} isLoading={isLoading} />}
          
          <Footer />
        </div>
      </div>
    </ErrorBoundary>;
};

export default Index;
